import i18n from "i18next";
import { type FC, useCallback } from "react";
import { useDispatch } from "react-redux";
import { useNavigate, useParams } from "react-router";

import { useCardDefinitionDeleteMutation } from "src/app/features/CardImplementation/subFeatures/CardDefinition/services/server/CardDefinitionApi";
import type { CardDefinitionEntity } from "src/app/features/CardImplementation/subFeatures/CardDefinition/types/CardDefinitionEntity";
import { type ExtendedColumnsType } from "../../../../../../../components";
import type { AntCustomFormField, CommonFieldAttributes } from "../../../../../../../components/Form/types/types";
import { PropertyTable } from "../../../../../../../components/Table/components/PropertyTable";
import {
    displayDateTimeValue,
    displayStatusValue,
} from "../../../../../../../components/Table/components/PropertyTable/transformations/cellTransformations";
import { Actions } from "../../../../../../../components/Table/types/types";
import { ResourceName } from "../../../../../../../core/router/config/ResourceName";
import { BASE_PATH, FORM_ACTION } from "../../../../../../../core/router/types/routerTypes";
import {
    showSuccessNotification,
    showWarningNotification,
} from "../../../../../../../core/setup/redux/state/notifications/notificationsState";

const getColumnsConfig = (t: Function): ExtendedColumnsType<CardDefinitionEntity>[] => {
    return [
        {
            title: t("cardImplementation.id"),
            key: "id",
            dataIndex: "id",
        },
        {
            title: t("cardImplementation.name"),
            key: "name",
            dataIndex: "name",
        },
        {
            title: t("cardImplementation.matchingPattern"),
            key: "matchingPattern",
            dataIndex: "matchingPattern",
        },
        {
            title: t("cardImplementation.status"),
            key: "status",
            dataIndex: "status",
            render: (cellValue) => displayStatusValue(cellValue),
        },
        {
            title: t("cardImplementation.order"),
            key: "order",
            dataIndex: "order",
        },
        {
            title: t("cardImplementation.changed"),
            dataIndex: "changed",
            key: "changed",
            render: (cellValue) => displayDateTimeValue(cellValue),
        },
    ];
};

export interface CardDefinitionTableProps extends AntCustomFormField<CardDefinitionEntity[]>, CommonFieldAttributes {}

export const CardDefinitionsTable: FC<CardDefinitionTableProps> = ({ value = [], onChange, readOnly }) => {
    const { id } = useParams();
    const dispatch = useDispatch();
    const navigate = useNavigate();

    const resourceUrl = `${ResourceName.CARD_IMPLEMENTATIONS}/${id}/${ResourceName.CARD_DEFINITIONS}`;

    const onDataChanged = (data: CardDefinitionEntity[]) => {
        if (onChange) {
            onChange(data);
        }
    };

    const onRowClick = (record: CardDefinitionEntity) => {
        navigate(`${BASE_PATH}/${resourceUrl}/${FORM_ACTION.EDIT}/${record.id}`);
    };

    const addCallback = () => {
        navigate(`${BASE_PATH}/${resourceUrl}/${FORM_ACTION.CREATE}`);
    };

    const [deleteTableItem] = useCardDefinitionDeleteMutation();

    const deleteCallback = (item: CardDefinitionEntity) => {
        deleteTableItem({
            cardImplementationId: id,
            id: item.id,
        })
            .unwrap()
            .then((payload) => performDeleteSuccess(payload))
            .catch(() => {
                dispatch(
                    showWarningNotification({
                        text: i18n.t("common.formDeleteError", {
                            id: item.id,
                        }),
                    }),
                );
            });

        const performDeleteSuccess = (payload: CardDefinitionEntity) => {
            if (payload) {
                notifyOkDelete(payload);
            }
            onDataChanged(value.filter((record) => record.id !== item.id));
        };
    };

    const notifyOkDelete = useCallback(
        (payload: CardDefinitionEntity) => {
            dispatch(
                showSuccessNotification({
                    text: i18n.t("common.formDeleteSuccess", {
                        id: payload.id,
                        resource: payload.name,
                    }),
                    autoCloseDisabled: false,
                }),
            );
        },
        [dispatch],
    );

    return (
        <PropertyTable<CardDefinitionEntity>
            getColumnsConfig={getColumnsConfig}
            name="cardDefinitions_table"
            propertyAsRowKey="id"
            tableData={value || []}
            readOnly={readOnly || false}
            onDataChanged={onDataChanged}
            onRow={(record: CardDefinitionEntity) => {
                return {
                    onClick: () => onRowClick(record),
                };
            }}
            resourceUrl={resourceUrl}
            actions={[Actions.DELETE, Actions.ADD, Actions.COPY]}
            actionCallbacks={{ add: addCallback, delete: (item?: CardDefinitionEntity) => item && deleteCallback(item) }}
        />
    );
};
